import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { getListAnalysis } from "@/api/lookups";
import type { ListAnalysisResponse, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ListAnalysisStatusBadge } from "@/components/analysis/ListAnalysisHistory";

export interface AnalysisCardViewProps {
  analysisId: UUID;
}

/** URL скачивания .docx — прямой переход браузера (attachment на бэке). */
export function analysisDocxHref(id: UUID): string {
  return `/api/list_analyses/${id}/export.docx`;
}

/** «Объект» разбора: сотрудник (manager) / клиент (client) / — (free). */
function objectDisplay(a: ListAnalysisResponse): string {
  if (a.mode === "manager") return a.employee_display ?? "—";
  if (a.mode === "client") return a.client_display ?? "—";
  return "—";
}

/**
 * Страница разбора /analyses/$analysisId (TM-4c1fb82b) — полноценное окно:
 * шапка (название, тип, объект, автор, дата, статус, модели/консенсус, токены)
 * и тело result_text, плюс кнопка «Скачать (.docx)» при status=done.
 */
export function AnalysisCardView({ analysisId }: AnalysisCardViewProps) {
  const { t, i18n } = useTranslation();
  const analysisQ = useQuery({
    queryKey: ["list-analysis", analysisId],
    queryFn: () => getListAnalysis(analysisId),
  });

  const a = analysisQ.data;

  return (
    <div className="space-y-4" data-testid="analysis-card">
      <Link
        to="/analyses"
        className="text-sm text-primary underline-offset-4 hover:underline"
        data-testid="analysis-card-back"
      >
        {t("listAnalysis.card.backToList")}
      </Link>

      {analysisQ.isError ? (
        <p role="alert" className="text-destructive">
          {t("listAnalysis.card.loadError")}
        </p>
      ) : a === undefined ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : (
        <>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <div className="space-y-1">
                <CardTitle data-testid="analysis-card-title">
                  {a.title ?? a.query_text}
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <ListAnalysisStatusBadge
                    status={a.status}
                    testId="analysis-card-status"
                  />
                  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {t(`listAnalysis.mode.${a.mode}`)}
                  </span>
                </div>
              </div>
              {a.status === "done" ? (
                <Button asChild size="sm" variant="outline">
                  <a
                    href={analysisDocxHref(a.id)}
                    download
                    data-testid="analysis-card-download"
                  >
                    {t("listAnalysis.card.download")}
                  </a>
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <Field
                  label={t("listAnalysis.registry.columns.object")}
                  value={objectDisplay(a)}
                />
                <Field
                  label={t("listAnalysis.registry.columns.author")}
                  value={a.author_display}
                />
                <Field
                  label={t("listAnalysis.registry.columns.date")}
                  value={new Date(a.created_at).toLocaleString(i18n.language)}
                />
                <Field
                  label={t("listAnalysis.card.models")}
                  value={
                    a.consensus_model !== null
                      ? `${a.llm_models.join(", ")} · ${t(
                          "listAnalysis.history.consensus",
                          { name: a.consensus_model },
                        )}`
                      : a.llm_models.join(", ")
                  }
                />
                <Field
                  label={t("listAnalysis.card.tokens")}
                  value={
                    a.input_tokens !== null || a.output_tokens !== null
                      ? `${a.input_tokens ?? "?"} → ${a.output_tokens ?? "?"}`
                      : null
                  }
                />
              </dl>
            </CardContent>
          </Card>

          {a.status === "error" && a.error_message !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {t("listAnalysis.history.errorMessage", {
                message: a.error_message,
              })}
            </p>
          ) : null}

          <Card>
            <CardContent className="space-y-3 pt-4 text-sm">
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("listAnalysis.viewer.queryHeading")}
                </h3>
                <p className="whitespace-pre-line">{a.query_text}</p>
              </div>
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("listAnalysis.viewer.resultHeading")}
                </h3>
                <pre className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 font-sans text-sm">
                  {a.result_text ?? ""}
                </pre>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/** One label/value pair in the card header. */
function Field({ label, value }: { label: string; value: string | null }) {
  const { t } = useTranslation();
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5">{value ?? t("common.dash")}</dd>
    </div>
  );
}
